// src/pages/SearchResultsPage.jsx
// #37 storefront search (2026-08-04): full results page for /search?q=...
// Three groups (products / available animals / care guides) rendered as
// light ProductCard-style rows using tokenized styles (theme-safe).
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { searchStorefront } from '../services/api';
import { searchSitePages } from '../constants/siteSearchIndex';

const sectionStyle = { marginBottom: '2rem' };
const headingStyle = {
  fontSize: '1.1rem', fontWeight: 700,
  color: 'var(--ff-text-primary, #212529)',
  borderBottom: '1px solid var(--ff-border-medium, #dee2e6)',
  paddingBottom: '6px', marginBottom: '10px',
};
const rowStyle = {
  display: 'flex', alignItems: 'center', gap: '14px',
  padding: '10px 12px',
  borderRadius: 'var(--ff-radius-md, 8px)',
  border: '1px solid var(--ff-border-medium, #dee2e6)',
  background: 'var(--ff-surface-primary, #fff)',
  color: 'var(--ff-text-primary, #212529)',
  textDecoration: 'none', marginBottom: '8px',
};

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const [input, setInput] = useState(q);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setInput(q);
    if (q.length < 2) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await searchStorefront(q);
        if (!cancelled) setResults(data);
      } catch (err) {
        if (!cancelled) setError('Search failed — please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (input.trim().length >= 2) setSearchParams({ q: input.trim() });
  };

  const pages = q.length >= 2 ? searchSitePages(q) : [];

  const total = (results
    ? (results.products?.length || 0) + (results.animals?.length || 0) + (results.care_guides?.length || 0)
    : 0) + pages.length;

  // #103 zero-inventory degradation: the server emits `group_order` (care
  // guides promoted to the top when the products group is empty). Unknown
  // names are dropped; payloads without the key keep today's order.
  const DEFAULT_GROUP_ORDER = ['products', 'animals', 'care_guides'];
  const serverGroupOrder = (Array.isArray(results?.group_order)
    ? results.group_order : []
  ).filter((g) => DEFAULT_GROUP_ORDER.includes(g));
  const apiGroupOrder = serverGroupOrder.length > 0
    ? serverGroupOrder : DEFAULT_GROUP_ORDER;

  return (
    // Batch 34 C2: 20px under the navbar like every other storefront page
    // (/frogs' .products-page is --ff-space-xl). It was a 90px band.
    <div className="container" style={{ paddingTop: 'var(--ff-space-xl)', paddingBottom: '3rem', maxWidth: '760px' }}>
      {/* #90c heading unification: one customer page-title treatment
          (.ff-heading-h1) instead of the inline 1.5rem one-off. */}
      <h1 className="ff-heading-h1">Search</h1>

      {/* Design System v1 (Phase 3): the box is one button tall (36px) and
          the row centres, so Search is the family's 36px — it used to
          stretch to the 46px box. */}
      <form onSubmit={submit} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '1rem 0 2rem' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--ff-text-secondary, #6c757d)' }} />
          <input
            type="search"
            aria-label="Search the store"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search the store…"
            style={{
              width: '100%', height: 'var(--ff-button-height-md)', padding: '0 12px 0 36px',
              borderRadius: 'var(--ff-radius-md, 8px)',
              border: '1px solid var(--ff-border-medium, #dee2e6)',
              background: 'var(--ff-surface-primary, #fff)',
              color: 'var(--ff-text-primary, #212529)',
            }}
          />
        </div>
        <button type="submit" className="ff-btn ff-btn--primary">Search</button>
      </form>

      {q.length < 2 && (
        <p style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>
          Enter at least 2 characters to search.
        </p>
      )}

      {loading && (
        <p style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>Searching…</p>
      )}

      {error && <div className="alert alert-warning" role="alert">{error}</div>}

      {!loading && !error && results && total === 0 && (
        <p style={{ color: 'var(--ff-text-secondary, #6c757d)' }}>
          No results for "{q}". Try a species name, common name, or locality.
        </p>
      )}

      {pages.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Pages</h2>
          {pages.map((pg) => (
            <Link key={pg.path} to={pg.path} style={rowStyle}>
              <span style={{ flex: 1, fontWeight: 600 }}>{pg.label}</span>
              <span style={{ color: 'var(--ff-link)', fontSize: '0.85rem', flexShrink: 0 }}>
                Go to page →
              </span>
            </Link>
          ))}
        </div>
      )}

      {/* #110b: rows carry thumbnails + the quoted species locality
          (storefront convention; server omits it when the name already
          embeds it).
          #103: sections render in the server's `group_order` (care guides
          first when nothing is for sale). */}
      {apiGroupOrder.map((groupKey) => (
        <React.Fragment key={groupKey}>
          {groupKey === 'products' && !loading && results && results.products?.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Products</h2>
          {results.products.map((p) => (
            <Link key={p.id} to={p.url} style={rowStyle}>
              {p.thumb_url && (
                <img src={p.thumb_url} alt="" width={56} height={56}
                     style={{ objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
              )}
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>
                  {p.name}
                  {p.locality && (
                    <span style={{ fontWeight: 400, color: 'var(--ff-text-secondary, #6c757d)' }}>
                      {' '}"{p.locality}"
                    </span>
                  )}
                </span>
                {p.common_name && (
                  <span style={{ fontSize: '0.85rem', color: 'var(--ff-text-secondary, #6c757d)' }}>
                    {p.common_name}
                  </span>
                )}
              </span>
              {p.price_display && (
                <span style={{ fontWeight: 600, flexShrink: 0 }}>{p.price_display}</span>
              )}
            </Link>
          ))}
        </div>
      )}

          {groupKey === 'animals' && !loading && results && results.animals?.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Available Animals</h2>
          {results.animals.map((a) => (
            <Link key={a.id} to={a.url} style={rowStyle}>
              {a.thumb_url && (
                <img src={a.thumb_url} alt="" width={56} height={56}
                     style={{ objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
              )}
              <span style={{ flex: 1, fontWeight: 600 }}>
                {a.name}
                {a.locality && (
                  <span style={{ fontWeight: 400, color: 'var(--ff-text-secondary, #6c757d)' }}>
                    {' '}"{a.locality}"
                  </span>
                )}
              </span>
              <span style={{ fontWeight: 600, flexShrink: 0 }}>{a.price_display}</span>
            </Link>
          ))}
        </div>
      )}

          {groupKey === 'care_guides' && !loading && results && results.care_guides?.length > 0 && (
        <div style={sectionStyle}>
          <h2 style={headingStyle}>Care Guides</h2>
          {results.care_guides.map((g) => (
            <Link key={g.species_id} to={g.url} style={rowStyle}>
              {g.thumb_url && (
                <img src={g.thumb_url} alt="" width={56} height={56}
                     style={{ objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
              )}
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontWeight: 600 }}>{g.title}</span>
                {g.common_name && (
                  <span style={{ fontSize: '0.85rem', color: 'var(--ff-text-secondary, #6c757d)' }}>
                    {g.common_name}
                  </span>
                )}
              </span>
              <span style={{ color: 'var(--ff-link)', fontSize: '0.85rem', flexShrink: 0 }}>
                Care guide →
              </span>
            </Link>
          ))}
        </div>
      )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default SearchResultsPage;
